import { Injectable, inject } from '@angular/core';
import { DocumentData } from '@angular/fire/firestore';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { isEqualJson, isUndefined } from 'src/app/shared/funcs/helpers';
import { AppDatabaseService } from './database/app-database.service';

@Injectable({
  providedIn: 'root',
})
export class AppParamsService {
  private readonly database = inject(AppDatabaseService);
  private readonly COLLECTION_NAME = 'params';
  private readonly watchers: Array<AppWatchers> = [];

  watchFor(fieldName: string, auctionId: string = 'lic001') {
    const sectionName = this.inferSectionName(fieldName);
    const key = `${auctionId}:${sectionName}:${fieldName}`;

    const existingIndex = this.watchers.findIndex((w) => w.key === key);
    if (existingIndex >= 0) {
      const existingWatcher = this.watchers[existingIndex];
      existingWatcher.subject.next();
      existingWatcher.subject.complete();
      this.watchers.splice(existingIndex, 1);
    }

    const completeSignal = new Subject<void>();
    const nextSignal = new Subject<any>();

    this.database.readLive(`${auctionId}/sections/${this.COLLECTION_NAME}/${sectionName}/fields/${fieldName}`, (doc) => {
      const data = doc.data();
      nextSignal.next(isUndefined(data?.['value']) ? data?.['default'] : data?.['value']);
    });

    const watcherObs = nextSignal.asObservable().pipe(
      distinctUntilChanged((a, b) => isEqualJson(a, b))
    );

    this.watchers.push({
      key: key,
      observable: watcherObs,
      subject: completeSignal,
    });

    return watcherObs;
  }

  watchForSection(sectionName: string, auctionId: string = 'lic001') {
    const key = `${auctionId}:${sectionName}:all`;

    // Limpiar watcher anterior si existe
    const existingIndex = this.watchers.findIndex((w) => w.key === key);
    if (existingIndex >= 0) {
      const existingWatcher = this.watchers[existingIndex];
      existingWatcher.subject.next();
      existingWatcher.subject.complete();
      this.watchers.splice(existingIndex, 1);
    }

    const subject = new Subject<void>();
    const newWatcher = this.database.readCollectionLive(`${auctionId}/sections/${this.COLLECTION_NAME}/${sectionName}/fields`).pipe(takeUntil(subject));

    this.watchers.push({
      key: key,
      observable: newWatcher,
      subject: subject,
    });

    return newWatcher;
  }

  dispose(
    fieldName: string,
    secName: string = '',
    auctionId: string = 'lic001'
  ) {
    const sectionName = secName || this.inferSectionName(fieldName);
    const key = `${auctionId}:${sectionName}:${fieldName}`;

    const instanceWatcherIndex = this.watchers.findIndex((w) => w.key === key);

    if (instanceWatcherIndex >= 0) {
      const watcher = this.watchers.splice(instanceWatcherIndex, 1)[0];
      watcher.subject.next();
      watcher.subject.complete();
    }
  }

  private inferSectionName(fieldName: any) {
    if (fieldName in AppParamGeneral) {
      return 'general';
    }
    if (fieldName in AppParamLots) {
      return 'lots';
    }
    if (fieldName in AppParamMarketing) {
      return 'marca';
    }
    if (fieldName in AppParamMultimedia) {
      return 'multimedia';
    }
    if (fieldName in AppParamSecurity) {
      return 'security';
    }

    throw Error(`field ${fieldName} not belogns to a valid section`);
  }
}

interface AppWatchers {
  key: string;
  observable: Observable<DocumentData | DocumentData[] | undefined>;
  subject: Subject<void>;
}

enum AppParamGeneral {
  contactNumber = 'contactNumber',
  lotDocument = 'lotDocument',
  lotDocumentName = 'lotDocumentName',
  terms = 'terms',
  wellcome = 'wellcome',
  privacyUrl = 'privacyUrl',
  exchangeRate = 'exchangeRate',
  primaryCurrencyType = 'primaryCurrencyType',
}

enum AppParamLots {
  lotResumeDownload = 'lotResumeDownload',
  lotResumeView = 'lotResumeView',
  lotResumeVideoView = 'lotResumeVideoView',
  lotAnticipatedMode = 'lotAnticipatedMode',
  lotScheduledBid = 'lotScheduledBid',
  lotBidCutRequest = 'lotBidCutRequest',
}

enum AppParamMarketing {
  notEventMessage = 'notEventMessage',
  notEventVideo = 'notEventVideo',
  primaryColor = 'primaryColor',
  secondaryColor = 'secondaryColor',
  buttonFontSize = 'buttonFontSize',
  contentFontSize = 'contentFontSize',
  infoPanelFontSize = 'infoPanelFontSize',
  titleFontSize = 'titleFontSize',
}

enum AppParamMultimedia {
  lotInfoLegend = 'lotInfoLegend',
  trEmbeded = 'trEmbeded',
}

enum AppParamSecurity {
  anonymAccessEnabled = 'anonymAccessEnabled',
  userBiometricAuth = 'userBiometricAuth',
  userSelfManagement = 'userSelfManagement',
}

export const AppParamItems = {
  AppParamGeneral,
  AppParamLots,
  AppParamMarketing,
  AppParamMultimedia,
  AppParamSecurity,
};
